import React, { useState, useEffect } from 'react';
import { Card, Table, Button, Modal, Form, Input, Select, Transfer, message, Tag } from 'antd';
import { PlusOutlined, UserOutlined } from '@ant-design/icons';
import ApiService from '../services/api';

const { Option } = Select;

const UserManagementBasic = () => {
  const [users, setUsers] = useState([]);
  const [trucks, setTrucks] = useState([]);
  const [loading, setLoading] = useState(false);
  const [trucksLoading, setTrucksLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [form] = Form.useForm();
  const [targetKeys, setTargetKeys] = useState([]);

  useEffect(() => {
    loadUsers();
    loadTrucks();
  }, []);

  const loadUsers = async () => {
    setLoading(true);
    try {
      const response = await ApiService.getUsers();
      setUsers(response);
    } catch (error) {
      message.error('Failed to load users: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const loadTrucks = async () => {
    setTrucksLoading(true);
    try {
      const response = await ApiService.getTrucks();
      console.log('Trucks loaded:', response);
      setTrucks(response.trucks || []);
    } catch (error) {
      console.error('Error loading trucks:', error);
      message.error('Failed to load trucks: ' + error.message);
    } finally {
      setTrucksLoading(false);
    }
  };

  const handleAddUser = () => {
    setTargetKeys([]);
    form.resetFields();
    setModalVisible(true);
  };

  const handleSubmit = async (values) => {
    try {
      const userData = {
        ...values,
        truckIds: targetKeys
      };

      const result = await ApiService.createUser(userData);
      message.success(`User created! Temp password: ${result.tempPassword}`);
      setModalVisible(false);
      loadUsers();
    } catch (error) {
      message.error('Failed to create user: ' + error.message);
    }
  };

  const handleResetPassword = async (userId) => {
    try {
      const response = await ApiService.resetPassword(userId);
      const result = typeof response === 'string' ? JSON.parse(response) : response;
      Modal.success({
        title: 'Password Reset Successful',
        content: `New password: ${result.newPassword}`,
      });
    } catch (error) {
      message.error('Failed to reset password: ' + error.message);
    }
  };

  const truckData = trucks.map(truck => ({
    key: truck.truck_id,
    title: `${truck.make} ${truck.model} (${truck.license_plate})`,
    description: `Truck ID: ${truck.truck_id}`
  }));

  const columns = [
    {
      title: 'Email',
      dataIndex: 'email',
      key: 'email',
      ellipsis: true
    },
    {
      title: 'User Type',
      dataIndex: 'user_type',
      key: 'user_type',
      render: (type) => (
        <Tag color={type === 'admin' ? 'blue' : type === 'owner' ? 'green' : 'default'}>
          {type?.toUpperCase()}
        </Tag>
      )
    },
    {
      title: 'Allowed Trucks',
      dataIndex: 'allowed_trucks',
      key: 'allowed_trucks'
    },
    {
      title: 'Created',
      dataIndex: 'created_at',
      key: 'created_at',
      render: (date) => date ? new Date(date).toLocaleDateString() : '-'
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => (
        <Button
          size="small"
          onClick={() => handleResetPassword(record.user_id)}
        >
          Reset Password
        </Button>
      )
    }
  ];

  return (
    <div>
      <Card
        title={
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <UserOutlined style={{ marginRight: 8 }} />
            User Management (Beta)
          </div>
        }
        extra={
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={handleAddUser}
          >
            Add User
          </Button>
        }
      >
        <Table
          columns={columns}
          dataSource={users}
          rowKey="user_id"
          loading={loading}
          pagination={{ pageSize: 10 }}
        />
      </Card>

      <Modal
        title="Add New User"
        open={modalVisible}
        onCancel={() => setModalVisible(false)}
        footer={null}
        width={800}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
        >
          <Form.Item
            name="email"
            label="Email"
            rules={[
              { required: true, message: 'Please enter email' },
              { type: 'email', message: 'Please enter valid email' }
            ]}
          >
            <Input />
          </Form.Item>

          <Form.Item
            name="firstName"
            label="First Name"
            rules={[{ required: true, message: 'Please enter first name' }]}
          >
            <Input />
          </Form.Item>

          <Form.Item
            name="lastName"
            label="Last Name"
            rules={[{ required: true, message: 'Please enter last name' }]}
          >
            <Input />
          </Form.Item>

          <Form.Item
            name="userType"
            label="User Type"
            rules={[{ required: true, message: 'Please select user type' }]}
          >
            <Select>
              <Option value="admin">Admin</Option>
              <Option value="owner">Owner</Option>
            </Select>
          </Form.Item>

          <Form.Item
            label="Truck Access (for Owners only)"
            help={`Select which trucks this user can view in reports. ${trucks.length} trucks available.`}
          >
            {trucksLoading ? (
              <div style={{ textAlign: 'center', padding: '20px' }}>Loading trucks...</div>
            ) : (
              <Transfer
                dataSource={truckData}
                titles={['Available Trucks', 'Allowed Trucks']}
                targetKeys={targetKeys}
                onChange={setTargetKeys}
                render={item => item.title}
                listStyle={{ width: 300, height: 300 }}
                showSearch
                filterOption={(inputValue, option) =>
                  option.title.toLowerCase().includes(inputValue.toLowerCase())
                }
              />
            )}
          </Form.Item>

          <Form.Item>
            <div style={{ textAlign: 'right' }}>
              <Button onClick={() => setModalVisible(false)} style={{ marginRight: 8 }}>
                Cancel
              </Button>
              <Button type="primary" htmlType="submit">
                Create User
              </Button>
            </div>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default UserManagementBasic;